import { campaignApiGet, campaignApiGetBlob, campaignApiPostForm } from '@/lib/api/campaign-api-client';
import { HttpError } from '@/lib/https';
import type {
  ContactImportEnvelopeApi,
  ContactImportFieldsDataApi,
  ContactImportJobDataApi,
  ContactImportPreviewDataApi,
} from '../types/contact-import-api.types';
import { parseContactImportApiErrors } from '../utils/contact-import-api-errors';

const assertImportSuccess = <T>(
  response: ContactImportEnvelopeApi<T> | null | undefined,
  fallback: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(fallback);
  }
  if (response.success === false) {
    const messages = parseContactImportApiErrors(response);
    throw new Error(messages[0] || response.message || fallback);
  }
  if (response.data == null || typeof response.data !== 'object') {
    throw new Error(`${fallback}: missing data payload`);
  }
  return response.data;
};

export const getContactImportErrorMessage = (error: unknown): string => {
  if (error instanceof HttpError) {
    const messages = parseContactImportApiErrors(error.error);
    return messages[0] || error.message || 'Import request failed';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Import request failed';
};

export const getContactImportFields = async (): Promise<ContactImportFieldsDataApi> => {
  const response = await campaignApiGet<ContactImportEnvelopeApi<ContactImportFieldsDataApi>>(
    '/v1/contacts/import/fields'
  );
  return assertImportSuccess(response, 'Failed to load import fields');
};

export const startContactImport = async (params: {
  file: File;
  mapping: Record<string, string>;
  listIds?: string[];
  idempotencyKey: string;
}): Promise<ContactImportJobDataApi> => {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('mapping', JSON.stringify(params.mapping));
  for (const listId of params.listIds ?? []) {
    if (listId.trim()) formData.append('listIds', listId.trim());
  }

  const response = await campaignApiPostForm<ContactImportEnvelopeApi<ContactImportJobDataApi>>(
    '/v1/contacts/import',
    formData,
    { 'Idempotency-Key': params.idempotencyKey }
  );
  return assertImportSuccess(response, 'Failed to start import');
};

/** The import's dry run: the upload's form fields, each row's outcome, nothing written. */
export const previewContactImport = async (params: {
  file: File;
  mapping: Record<string, string>;
  listIds?: string[];
}): Promise<ContactImportPreviewDataApi> => {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('mapping', JSON.stringify(params.mapping));
  for (const listId of params.listIds ?? []) {
    if (listId.trim()) formData.append('listIds', listId.trim());
  }

  const response = await campaignApiPostForm<ContactImportEnvelopeApi<ContactImportPreviewDataApi>>(
    '/v1/contacts/import/preview',
    formData
  );
  return assertImportSuccess(response, 'Failed to preview import');
};

export const getContactImportJob = async (jobId: string): Promise<ContactImportJobDataApi> => {
  const id = jobId.trim();
  if (!id) throw new Error('Import job id is required');
  const response = await campaignApiGet<ContactImportEnvelopeApi<ContactImportJobDataApi>>(
    `/v1/contacts/import/${encodeURIComponent(id)}`
  );
  return assertImportSuccess(response, 'Failed to fetch import job');
};

export const downloadContactImportErrors = async (jobId: string): Promise<Blob> => {
  const id = jobId.trim();
  if (!id) throw new Error('Import job id is required');
  return campaignApiGetBlob(`/v1/contacts/import/${encodeURIComponent(id)}/errors`);
};
